// Giải mã barcode/QR từ một ảnh tĩnh (ảnh chọn từ thư viện hoặc vừa chụp).
// Lazy-import html5-qrcode để thư viện (~80KB) không vào bundle chính — chỉ tải khi dùng.
//
// Ảnh chụp từ camera điện thoại thường rất lớn (~12MP). html5-qrcode giải mã ĐỒNG BỘ trên
// main thread → ảnh to làm treo UI vài giây và thường trượt (mã nhỏ so với ảnh, có thể bị
// xoay theo EXIF). Vì vậy ta THU NHỎ + vẽ lại qua canvas trước khi decode:
//   - canvas tự áp EXIF orientation (trình duyệt hiện đại) → ảnh thẳng, dễ nhận hơn.
//   - ảnh nhỏ → decode nhanh, hết đơ.

let seq = 0

// Cạnh dài tối đa sau khi thu nhỏ: đủ nét cho barcode/QR nhưng decode nhanh.
const MAX_DIM = 1600

const loadImage = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => resolve({ img, url })
    img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('Không đọc được ảnh'))
    }
    img.src = url
})

// Thu nhỏ ảnh về <= maxDim (giữ tỉ lệ). Trả về File mới; nếu lỗi/không cần thì trả file gốc.
// forceJpeg: vẽ lại qua canvas kể cả ảnh đã nhỏ (ảnh thư viện PNG/HEIC → JPEG, kích thước dự đoán được).
export const downscaleFile = async (file, { maxDim = MAX_DIM, quality = 0.92, forceJpeg = false } = {}) => {
    let loaded
    try {
        loaded = await loadImage(file)
    } catch (e) {
        return file
    }
    const { img, url } = loaded
    // try/finally để object URL LUÔN được revoke kể cả khi getContext/drawImage throw
    // (hết bộ nhớ canvas trên máy yếu) — tránh rò rỉ blob ảnh ~12MP mỗi lần decode lỗi.
    let canvas
    try {
        const w = img.naturalWidth
        const h = img.naturalHeight
        const maxSide = Math.max(w, h)
        if (!maxSide || (maxSide <= maxDim && !forceJpeg)) {
            return file
        }
        const scale = maxSide > maxDim ? maxDim / maxSide : 1
        const cw = Math.round(w * scale)
        const ch = Math.round(h * scale)
        canvas = document.createElement('canvas')
        canvas.width = cw
        canvas.height = ch
        canvas.getContext('2d').drawImage(img, 0, 0, cw, ch)
    } catch (e) {
        return file
    } finally {
        URL.revokeObjectURL(url)
    }

    const blob = await new Promise((resolve) => {
        if (canvas.toBlob) canvas.toBlob(resolve, 'image/jpeg', quality)
        else resolve(null)
    })
    if (!blob) return file
    return new File([blob], 'scan.jpg', { type: 'image/jpeg' })
}

// html5-qrcode (scanFile) chỉ trả về DUY NHẤT 1 mã/ảnh. Giải mã 1 mã từ ảnh đã thu nhỏ.
const decodeSingle = async (prepared) => {
    const { Html5Qrcode } = await import('html5-qrcode')

    seq += 1
    const id = `request-photo-file-reader-${seq}`
    const el = document.createElement('div')
    el.id = id
    el.style.display = 'none'
    document.body.appendChild(el)

    const scanner = new Html5Qrcode(id, { verbose: false })
    try {
        const text = await scanner.scanFile(prepared, false)
        return (text || '').trim()
    } finally {
        try {
            scanner.clear()
        } catch (e) {
            // bỏ qua lỗi dọn dẹp
        }
        if (el.parentNode) el.parentNode.removeChild(el)
    }
}

// Các format mã đơn cần đọc (QR + barcode 1D) — dùng cho BarcodeDetector native.
const WANTED_FORMATS = ['qr_code', 'code_128', 'code_39', 'ean_13']

// Polyfill BarcodeDetector (barcode-detector + zxing-cpp WASM ~1MB, tự host ở public/vendor) cho máy KHÔNG có
// BarcodeDetector native (Safari iPhone, Chrome PC): html5-qrcode trả 0 mã khi ảnh có 2 QR dán chồng.
// Không ghi đè window.BarcodeDetector; chỉ tải khi nơi gọi bật option polyfill.
const POLYFILL_DIR = `${process.env.PUBLIC_URL || ''}/vendor/barcode-detector-3.2.2`
let polyfillPromise = null

const loadScript = (src) => new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = resolve
    script.onerror = () => {
        if (script.parentNode) script.parentNode.removeChild(script)
        reject(new Error(`Không tải được ${src}`))
    }
    document.head.appendChild(script)
})

// Trả class BarcodeDetector của polyfill (đã nạp xong WASM), hoặc null nếu không tải được → caller fallback html5-qrcode.
const loadPolyfillDetector = () => {
    if (!polyfillPromise) {
        polyfillPromise = (async () => {
            if (typeof WebAssembly === 'undefined') return null
            if (!window.BarcodeDetectionAPI) await loadScript(`${POLYFILL_DIR}/ponyfill.js`)
            const api = window.BarcodeDetectionAPI
            await api.prepareZXingModule({
                overrides: {
                    locateFile: (path, prefix) => (path.endsWith('.wasm') ? `${POLYFILL_DIR}/${path}` : prefix + path),
                },
                fireImmediately: true,
            })
            return api.BarcodeDetector
        })().catch(() => {
            polyfillPromise = null // mạng chập chờn: lần đọc sau thử tải lại
            return null
        })
    }
    return polyfillPromise
}

const hasNativeDetector = () => typeof window !== 'undefined' && !!window.BarcodeDetector

// Tải sẵn polyfill (vd khi mở màn) để ảnh đầu tiên không phải chờ ~1MB WASM. Máy có native thì bỏ qua.
export const preloadBarcodePolyfill = () => {
    if (!hasNativeDetector()) loadPolyfillDetector()
}

// BarcodeDetector (native hoặc polyfill) đọc được NHIỀU mã trong 1 ảnh —
// cần cho yêu cầu "ảnh có nhiều QR". Trả mảng mã (có thể rỗng) nếu detector chạy được;
// trả null nếu KHÔNG hỗ trợ / lỗi → caller fallback về giải mã 1 mã (decodeSingle).
const detectMultiple = async (file, BD) => {
    if (!BD) return null
    try {
        // Chỉ khai báo format mà thiết bị thật sự hỗ trợ (khai báo format lạ sẽ throw).
        let formats = WANTED_FORMATS
        if (typeof BD.getSupportedFormats === 'function') {
            const supported = await BD.getSupportedFormats()
            formats = WANTED_FORMATS.filter(f => supported.indexOf(f) !== -1)
            if (!formats.length) return null // thiết bị không đọc được QR/barcode qua API này
        }
        const detector = new BD({ formats })
        const bitmap = await createImageBitmap(file)
        try {
            const results = await detector.detect(bitmap)
            return (results || []).map(r => (r.rawValue || '').trim()).filter(Boolean)
        } finally {
            if (bitmap && typeof bitmap.close === 'function') bitmap.close()
        }
    } catch (e) {
        return null // lỗi native → để caller fallback
    }
}

// Giải mã TẤT CẢ mã barcode/QR trong 1 ảnh. Trả mảng string (đã trim, đã bỏ rỗng).
// - Có BarcodeDetector native: đọc được nhiều mã → validate "các QR có cùng 1 đơn không" (caller xử lý).
// - Không có native + polyfill: đọc nhiều mã bằng polyfill; polyfill lỗi hoặc ra 0 mã thì thử html5-qrcode.
// - Còn lại: fallback về 1 mã (html5-qrcode) → mảng 0 hoặc 1 phần tử (degrade an toàn).
// prepared: file đã thu nhỏ sẵn (vd qua downscaleFile) → bỏ bước thu nhỏ lần 2.
export const decodeImageCodes = async (file, { prepared: isPrepared = false, polyfill = false } = {}) => {
    const prepared = isPrepared ? file : await downscaleFile(file)
    if (hasNativeDetector()) {
        const many = await detectMultiple(prepared, window.BarcodeDetector)
        if (many) return many
    } else if (polyfill) {
        const many = await detectMultiple(prepared, await loadPolyfillDetector())
        if (many && many.length) return many
    }
    const one = await decodeSingle(prepared)
    return one ? [one] : []
}

// Giải mã 1 mã từ ảnh (giữ API cũ cho nơi chỉ cần 1 mã).
export const decodeImageFile = async (file) => {
    const prepared = await downscaleFile(file)
    return decodeSingle(prepared)
}

export default decodeImageFile
